<template>
  <v-container class="service-panel pa-0" fluid>
    <loading-box v-if="MachineServiceHistories == null" :loading="true" />

    <div v-else>
      <!-- Open Jobs Section -->
      <div class="section-header d-flex align-center mb-4">
        <v-icon color="primary" class="mr-2">mdi-clipboard-text-clock-outline</v-icon>
        <h3 class="text-h6 font-weight-medium ma-0">
          {{ $t("text.openjobs") }}
          <v-chip v-if="openJobs && openJobs.length > 0" small color="primary" dark class="ml-2">
            {{ openJobs.length }}
          </v-chip>
        </h3>
        <v-spacer />
        <v-btn color="primary" depressed rounded small @click="newjobdialog = true">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t("text.orderservice") }}
        </v-btn>
      </div>

      <new-job-dialog :machine="machine" :machines="[machine]" v-if="newjobdialog" @exit="closeNewJobDialog" />

      <!-- Empty state for open jobs -->
      <v-card v-if="openJobs && openJobs.length === 0" flat outlined class="text-center pa-6 mb-6 empty-state-card">
        <v-icon size="48" color="grey lighten-1">mdi-check-circle-outline</v-icon>
        <div class="text-body-1 grey--text mt-2">{{ $t("text.openjobs") }}: 0</div>
      </v-card>

      <!-- Open Job Cards -->
      <v-card v-for="item in openJobs" :key="'open-' + item.Id" outlined class="mb-4 open-job-card" hover
        @click="openJob(item)">
        <div class="card-accent-bar" :style="{ backgroundColor: $vuetify.theme.themes.light.primary }" />
        <v-card-text class="pa-4">
          <v-row no-gutters align="start">
            <v-col>
              <!-- Top row: order id + chips -->
              <div class="d-flex align-center flex-wrap mb-1">
                <span class="text-subtitle-2 font-weight-bold mr-2" style="color: rgba(0,0,0,.87);">
                  {{ item.OrderId }}
                </span>
                <JobTypeChip :typeId="item.JobTypeId" :typeName="item.JobTypeName" class="mr-1" />
                <v-chip x-small outlined :color="statusColorForName(item.StatusName)" class="font-weight-medium">
                  {{ item.StatusName }}
                </v-chip>
              </div>

              <!-- Display name -->
              <div class="text-body-2 mb-2" style="color: rgba(0,0,0,.6);">
                {{ item.DisplayName }}
              </div>

              <!-- Info grid -->
              <v-row dense class="text-caption" style="color: rgba(0,0,0,.54);">
                <v-col cols="auto" class="d-flex align-center mr-4">
                  <v-icon x-small class="mr-1" color="grey">mdi-calendar-plus</v-icon>
                  {{ $t("text.registered") }}: {{ formatDate(item.CreatedDate) }}
                </v-col>
                <v-col cols="auto" class="d-flex align-center mr-4" v-if="item.EstimatedTime">
                  <v-icon x-small class="mr-1" color="grey">mdi-clock-outline</v-icon>
                  {{ $t("text.estimated_time") }}: {{ item.EstimatedTime }} {{ $t("text.hours") }}
                </v-col>
                <v-col cols="auto" class="d-flex align-center" v-if="item.JobPriority">
                  <v-icon x-small class="mr-1" color="grey">mdi-flag</v-icon>
                  {{ $t("text.priority") }}: {{ item.JobPriority }}
                </v-col>
              </v-row>

              <!-- Customer comment -->
              <div v-if="item.CustomerComment" class="mt-2">
                <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
                  {{ $t("text.customer_comment") }}
                </div>
                <div class="text-body-2 comment-text mt-1">{{ item.CustomerComment }}</div>
              </div>

              <!-- Delivery Address -->
              <div
                v-if="item.CustomerDeliveryAdressStreet || item.CustomerDeliveryAdressCity"
                class="mt-2 d-flex align-start"
              >
                <v-icon x-small class="mr-1 mt-1" color="grey">mdi-map-marker-outline</v-icon>
                <span class="text-caption" style="color: rgba(0,0,0,.54);">
                  {{ [item.CustomerDeliveryAdressName, item.CustomerDeliveryAdressStreet, item.CustomerDeliveryAdressZip, item.CustomerDeliveryAdressCity].filter(Boolean).join(', ') }}
                </span>
              </div>
            </v-col>

            <v-col cols="auto" class="ml-2">
              <v-btn icon small color="primary">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- History Section -->
      <v-divider class="my-6" />

      <div class="section-header d-flex align-center mb-4">
        <v-icon color="grey darken-1" class="mr-2">mdi-history</v-icon>
        <h3 class="text-h6 font-weight-medium ma-0">
          {{ $t("text.history") }}
          <v-chip v-if="closedJobs && closedJobs.length > 0" small outlined class="ml-2">
            {{ closedJobs.length }}
          </v-chip>
        </h3>
      </div>

      <!-- Empty state for history -->
      <v-card v-if="!closedJobs || closedJobs.length === 0" flat outlined class="text-center pa-6 mb-6 empty-state-card">
        <v-icon size="48" color="grey lighten-1">mdi-archive-outline</v-icon>
        <div class="text-body-1 grey--text mt-2">{{ $t("text.history") }}: 0</div>
      </v-card>

      <!-- History Cards -->
      <v-expansion-panels v-else flat accordion multiple>
        <v-expansion-panel v-for="item in closedJobs" :key="'hist-' + item.Id" class="history-panel mb-3">
          <v-expansion-panel-header class="pa-4">
            <v-row no-gutters align="center">
              <v-col cols="auto" class="mr-3">
                <v-avatar size="36" :color="statuscolor(item.Job.StatusId)" class="white--text">
                  <v-icon small dark>{{ item.Job.StatusId === 100 ? 'mdi-check-all' : 'mdi-check' }}</v-icon>
                </v-avatar>
              </v-col>
              <v-col>
                <div class="d-flex align-center flex-wrap">
                  <span class="text-subtitle-2 font-weight-bold mr-2" style="color: rgba(0,0,0,.87);">
                    {{ item.Job.OrderId || item.ServiceHistoryOrderReference }}
                  </span>
                  <v-chip
                    v-if="item.Job.StatusId === 100"
                    x-small color="jobstatus_closed_color" dark
                    style="border-radius: 4px;"
                  >
                    {{ $t("text.closed") }}
                  </v-chip>
                </div>
                <div class="text-caption" style="color: rgba(0,0,0,.54);">
                  {{ item.Job.DisplayName }}
                </div>
              </v-col>
              <v-col cols="auto" class="text-right" :class="{ 'd-none': isPhoneBool }">
                <div class="text-caption" style="color: rgba(0,0,0,.54);">
                  {{ formatDate(item.CreatedDate) }}
                  <span v-if="item.CompletedDate"> — {{ formatDate(item.CompletedDate) }}</span>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-divider class="mb-3" />

            <!-- Timeline-like dates row -->
            <v-row dense class="mb-3" v-if="$store.state.userInfo.IsJobTimelineEnabled">
              <v-col cols="12" sm="6" md="3" v-if="item.CreatedDate">
                <div class="date-block">
                  <div class="text-overline" style="line-height: 1.2;">{{ $t("text.registered") }}</div>
                  <div class="text-body-2 font-weight-medium">{{ formatDate(item.CreatedDate) }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3" v-if="item.PlannedDate">
                <div class="date-block">
                  <div class="text-overline" style="line-height: 1.2;">{{ $t("text.scheduledfor") }}</div>
                  <div class="text-body-2 font-weight-medium">{{ formatDate(item.PlannedDate) }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3" v-if="item.CompletedDate">
                <div class="date-block">
                  <div class="text-overline" style="line-height: 1.2;">Completed</div>
                  <div class="text-body-2 font-weight-medium">{{ formatDate(item.CompletedDate) }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3" v-if="item.ClosedDate">
                <div class="date-block">
                  <div class="text-overline" style="line-height: 1.2;">{{ $t("text.closed") }}</div>
                  <div class="text-body-2 font-weight-medium">{{ formatDate(item.ClosedDate) }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- Comments section -->
            <v-row dense>
              <v-col cols="12" sm="6" v-if="item.CustomerComment || item.Job.CustomerComment">
                <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
                  {{ $t("text.customer_comment") }}
                </div>
                <div class="text-body-2 comment-text mt-1">
                  {{ item.CustomerComment || item.Job.CustomerComment || '—' }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" v-if="item.InvoiceComment || item.Job.InvoiceComment">
                <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
                  {{ $t("text.invocie_comment") }}
                </div>
                <div class="text-body-2 comment-text mt-1">
                  {{ item.InvoiceComment || item.Job.InvoiceComment || '—' }}
                </div>
              </v-col>
            </v-row>

            <!-- Delivery address -->
            <div
              v-if="item.Job.CustomerDeliveryAdressStreet || item.Job.CustomerDeliveryAdressCity"
              class="mt-3"
            >
              <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
                <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>
                {{ $t("text.delivery_address") }}
              </div>
              <div class="text-body-2 mt-1">
                {{ [item.Job.CustomerDeliveryAdressName, item.Job.CustomerDeliveryAdressStreet, item.Job.CustomerDeliveryAdressZip, item.Job.CustomerDeliveryAdressCity, item.Job.CustomerDeliveryAdressCountry].filter(Boolean).join(', ') }}
              </div>
            </div>

            <!-- Cost summary -->
            <div v-if="showPrice && hasCostData(item)" class="mt-4">
              <div class="text-caption font-weight-medium mb-2" style="color: rgba(0,0,0,.54);">
                {{ $t("text.cost") }}
              </div>
              <v-row dense>
                <v-col cols="auto" v-if="item.KPIWorkCost != null" class="mr-4">
                  <v-card flat outlined class="pa-3 text-center cost-card">
                    <div class="text-overline" style="line-height: 1.2;">{{ $t("text.Service") }}</div>
                    <div class="text-subtitle-1 font-weight-bold primary--text">
                      {{ formatCurrency(item.KPIWorkCost) }}
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="auto" v-if="item.KPIPartCost != null" class="mr-4">
                  <v-card flat outlined class="pa-3 text-center cost-card">
                    <div class="text-overline" style="line-height: 1.2;">{{ $t("text.Parts") }}</div>
                    <div class="text-subtitle-1 font-weight-bold primary--text">
                      {{ formatCurrency(item.KPIPartCost) }}
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="auto" v-if="item.KPIDrivingCost != null">
                  <v-card flat outlined class="pa-3 text-center cost-card">
                    <div class="text-overline" style="line-height: 1.2;">Driving</div>
                    <div class="text-subtitle-1 font-weight-bold primary--text">
                      {{ formatCurrency(item.KPIDrivingCost) }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Parts table -->
            <div v-if="item.MachineServiceHistoriesParts && item.MachineServiceHistoriesParts.length > 0" class="mt-4">
              <div class="text-caption font-weight-medium mb-2" style="color: rgba(0,0,0,.54);">
                {{ $t("text.articles") }}
              </div>
              <v-simple-table dense class="parts-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">{{ $t("text.article") }}</th>
                      <th class="text-left">{{ $t("text.article_number") }}</th>
                      <th class="text-right">{{ $t("text.amount") }}</th>
                      <th v-if="showPrice" class="text-right">{{ $t("text.price") }}</th>
                      <th v-if="showPrice" class="text-right">{{ $t("text.total_sum") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="part in item.MachineServiceHistoriesParts" :key="part.Id">
                      <td>
                        <div class="d-flex align-center">
                          <v-avatar v-if="part.Article && part.Article.ArticleImage" size="28" class="mr-2" tile rounded>
                            <v-img :src="part.Article.ArticleImage" />
                          </v-avatar>
                          {{ part.ArticleDescription || (part.Article && part.Article.ArticleDescription) }}
                        </div>
                      </td>
                      <td class="text-caption">{{ part.Article && part.Article.ArticleNumber || '—' }}</td>
                      <td class="text-right">{{ part.ArticleQtyOrdered }}</td>
                      <td v-if="showPrice" class="text-right">{{ formatCurrency(part.ArticlePriceOut) }}</td>
                      <td v-if="showPrice" class="text-right font-weight-medium">{{ formatCurrency(part.Total) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Actions -->
            <div class="d-flex justify-end mt-4">
              <DownloadServiceReportBtn v-if="item.ServiceReport || item.Job" :job="item.Job" class="mr-2" />
              <v-btn small outlined color="primary" @click="openJob(item.Job)" style="text-transform: none;">
                <v-icon left small>mdi-open-in-new</v-icon>
                {{ $t("text.open") }}
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <JobDialog />

    <v-snackbar v-model="snackbar" color="primary" rounded>
      <v-icon dark small class="mr-2">mdi-check-circle</v-icon>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>


<script>
import http from "@/api/client";
import JobDialog from "@/components/Dialogs/JobDialog";
import NewJobDialog from "@/components/Dialogs/NewJobDialogEx.vue";
import LoadingBox from "@/components/LoadingBox.vue";
import JobTypeChip from "@/components/JobTypeChip.vue";
import DownloadServiceReportBtn from "@/components/DownloadServiceReportBtn.vue";

export default {
  name: "ServicePanel",
  components: {
    "loading-box": LoadingBox,
    NewJobDialog,
    JobDialog,
    JobTypeChip,
    DownloadServiceReportBtn,
  },

  data() {
    return {
      snackbar: false,
      snackbarMessage: null,
      openJobs: null,
      machineSelection: null,
      loading: false,
      newjobdialog: false,
      selectedJob: null,
      machine: null,
      MachineServiceHistories: null,
    };
  },

  mounted() {
    this.init();
    if (this.machine) this.updateServiceHistories(this.machine);
    else this.$emit("updateMachine");
  },

  methods: {
    closeNewJobDialog(message) {
      this.newjobdialog = false;
      this.snackbarMessage = message;
      this.snackbar = true;
      this.updateServiceHistories(this.machine);
    },

    updateServiceHistories(machine) {
      if (!machine) return;
      this.loading = true;
      http()
        .get("api2/customer/machines/history/" + machine.Id)
        .then((response) => {
          this.MachineServiceHistories = response.data;
          if (this.closedJobs && this.closedJobs.length > 0)
            this.selectedJob = this.closedJobs[0];
          this.loading = false;
        });

      http()
        .get("api2/customer/jobs/openmachinejobs/" + machine.Id)
        .then((response) => {
          this.openJobs = response.data;
        });
    },

    init() {
      this.machine = this.$route.params.Machine;
    },

    openJob(item) {
      this.$router.push({ params: { jobId: item.Id } });
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    hasCostData(item) {
      return (
        item.KPIWorkCost != null ||
        item.KPIPartCost != null ||
        item.KPIDrivingCost != null
      );
    },

    statusColorForName(statusName) {
      if (!statusName) return "grey";
      const name = statusName.toLowerCase();
      if (name.includes("new")) return "jobstatus_new_color";
      if (name.includes("drive")) return "jobstatus_drive_color";
      if (name.includes("progress")) return "jobstatus_in_progress_color";
      if (name.includes("waiting")) return "jobstatus_waiting_color";
      if (name.includes("planned")) return "jobstatus_planned_color";
      if (name.includes("completed")) return "jobstatus_completed_color";
      if (name.includes("closed")) return "jobstatus_closed_color";
      return "grey";
    },
  },

  computed: {
    closedJobs() {
      if (
        this.MachineServiceHistories != null &&
        this.MachineServiceHistories.length > 0
      ) {
        return this.MachineServiceHistories.filter(
          (e) => e.Job?.StatusId > 30 && e.Job?.StatusId <= 100
        ).sort((a, b) => b.Id - a.Id);
      } else {
        return null;
      }
    },

    showPrice() {
      return this.$store.state.userInfo && this.$store.state.userInfo.ShowPrices;
    },

    partsHeader() {
      var headers = [
        {
          text: this.$t("text.article"),
          value: "Article.ArticleDescription",
        },
        {
          text: this.$t("text.amount"),
          value: "ArticleQtyOrdered",
        },
      ];
      if (this.showPrice) {
        headers.push({
          text: this.$t("text.price"),
          value: "ArticlePriceOut",
        });
      }
      return headers;
    },
  },

  watch: {
    machine(val) {
      this.updateServiceHistories(val);
    },
    $route() {
      this.init();
    },
  },
};
</script>


<style scoped>
.service-panel {
  max-width: 900px;
}

.section-header h3 {
  letter-spacing: 0.01em;
}

.open-job-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px !important;
  transition: box-shadow 0.2s ease;
  cursor: pointer;
}

.open-job-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.card-accent-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}

.empty-state-card {
  border-radius: 8px !important;
  border-style: dashed !important;
}

.comment-text {
  background-color: #f9f9fb;
  border-left: 3px solid #e0e0e0;
  padding: 8px 12px;
  border-radius: 0 4px 4px 0;
  white-space: pre-wrap;
  max-height: 120px;
  overflow-y: auto;
}

.history-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  overflow: hidden;
}

.history-panel::before {
  box-shadow: none !important;
}

.date-block {
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-radius: 6px;
  border-left: 3px solid var(--v-primary-base);
}

.cost-card {
  min-width: 100px;
  border-radius: 8px !important;
}

.parts-table {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.parts-table thead th {
  background-color: #f5f7fa !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
